import styled from 'styled-components';

/**
 * Unified toolbar row — search · segmented status · divider · filter chips ·
 * spacer · primary "+" action. Mirrors the prototype's `.listpage__bar`
 * exactly: no border, no background, no padding. Just a flex row with a tight
 * 6px gap and a 14px bottom margin. The toolbar itself is unchromed; chrome
 * belongs to the children (the search pill, the segmented control, the
 * filled "+" icon button).
 */
export const ToolbarRoot = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 14px;
  flex-wrap: wrap;
`;

/**
 * Pushes the trailing primary action (the round filled "+") to the right
 * edge of the toolbar. Mirrors the prototype's `.bar__spacer` —
 * `flex: 1 1 auto` with a collapsible min-width so it disappears gracefully
 * on narrow viewports.
 */
export const ToolbarSpacer = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

/**
 * Anchor for the Sort dropdown — `position: relative` so the floating
 * `<ChipMenu>` can `absolute`-position itself beneath the trigger.
 */
export const SortAnchor = styled.div`
  position: relative;
  display: inline-flex;
`;

/**
 * "Sort: Newest" chip trigger — mirrors the prototype's `.icon-chip` exactly.
 * Borderless 30px-tall pill with a 6px gap between the icon and the label.
 * Hover/open state uses a soft on-surface tint, never an outline. Active
 * (a non-default sort applied) bumps to a primary tint.
 */
export const SortTrigger = styled.button<{ $isOpen: boolean; $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  transition:
    background 140ms ease,
    color 140ms ease;

  ${({ theme: { colors = {} }, $isOpen, $isActive }) => {
    const onSurface = colors.onSurface ?? '#E4E1ED';
    const onSurfaceVariant = colors.onSurfaceVariant ?? '#C7C4D7';
    const primary = colors.primary ?? '#C1C1FF';

    // Three-state resting style — flattened from a nested ternary (Sonar)
    // into an explicit if/else chain for readability.
    let restingBg: string;
    let restingFg: string;
    if ($isActive) {
      restingBg = `color-mix(in oklch, var(--primary, ${primary}) 16%, transparent)`;
      restingFg = primary;
    } else if ($isOpen) {
      restingBg = `color-mix(in oklch, ${onSurface} 8%, transparent)`;
      restingFg = onSurface;
    } else {
      restingBg = 'transparent';
      restingFg = onSurfaceVariant;
    }

    return `
      background: ${restingBg};
      color: ${restingFg};

      &:hover {
        background: ${
          $isActive
            ? 'color-mix(in oklch, var(--primary, ' +
              primary +
              ') 22%, transparent)'
            : 'color-mix(in oklch, ' + onSurface + ' 6%, transparent)'
        };
        color: ${$isActive ? primary : onSurface};
      }

      &:focus-visible {
        outline: 2px solid ${primary};
        outline-offset: 2px;
      }

      & > svg {
        width: 18px;
        height: 18px;
      }
    `;
  }}
`;

/**
 * Floating wrapper that pins the ChipMenu directly beneath the trigger.
 * Matches the prototype's `.chip-wrap` — small top offset so the menu
 * doesn't tag onto the trigger border.
 */
export const SortMenuPosition = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 6px;
  z-index: 10;
`;
